import {
  Building2,
  Circle,
  Tag,
  User,
  UserCog,
  Warehouse,
  Building,
  ClipboardList,
  ClipboardClock,
  ClipboardPenLine,
  ClipboardPaste,
  File,
  FolderClosed,
  HeartHandshake,
  Shrimp,
  Bubbles,
  RefreshCw,
} from "lucide-react";

import { type UnifiedNavItem } from "../components/layout/Sidebar";
import { PERMISSIONS } from "@/lib/permissions";

export const navItems: UnifiedNavItem[] = [
  {
    to: "/my-tasks",
    label: "My Tasks",
    icon: ClipboardList,
    permission: PERMISSIONS.MYTASK_VIEW,
  },
  {
    to: "/production/cycles",
    label: "Cycles",
    icon: RefreshCw,
    section: "Production",
  },
  {
    to: "/production/tank-population",
    label: "Tank Population",
    icon: Warehouse,
    section: "Production",
  },
  {
    to: "/production/receiving-tank",
    label: "Receiving Tank",
    icon: Warehouse,
    section: "Production",
  },
  {
    to: "/production/maturation",
    label: "Maturation",
    icon: HeartHandshake,
    section: "Production",
  },
  {
    to: "/production/mortality",
    label: "Mortality",
    icon: ClipboardList,
    section: "Production",
  },
  {
    to: "/programs",
    label: "Programs",
    icon: FolderClosed,
    section: "Breeding Program",
    permission: PERMISSIONS.BREEDPROGRAM_VIEW,
  },
  {
    label: "Maturation",
    icon: HeartHandshake,
    section: "Breeding Program",
    children: [
      {
        to: "/maturation/mating-frequency",
        label: "Mating Freq",
        permission: PERMISSIONS.BREEDMATING_VIEW,
      },
      {
        to: "/maturation/mortality-entry",
        label: "Mortality Entry",
        permission: PERMISSIONS.BREEDMORTALITY_VIEW,
      },
      {
        to: "/maturation/feeding-entry",
        label: "Feeding Entry",
        permission: PERMISSIONS.BREEDFEEDING_VIEW,
      },
      {
        to: "/maturation/weight-biomass-entry",
        label: "Weight & Biomass",
        permission: PERMISSIONS.BREEDWEIGHT_VIEW,
      },
    ],
  },
  {
    to: "/batches",
    label: "Batches",
    icon: Bubbles,
    section: "Breeding Program",
    permission: PERMISSIONS.BREEDBATCH_VIEW,
  },
  {
    to: "/pl-trial",
    label: "PL Trial",
    icon: ClipboardPaste,
    section: "Breeding Program",
    permission: PERMISSIONS.CAGETRIAL_VIEW,
  },
  {
    to: "/cage-trial",
    label: "Cage Trial",
    icon: ClipboardPaste,
    section: "Breeding Program",
    permission: PERMISSIONS.CAGETRIAL_VIEW,
  },
  {
    to: "/disease-challenges",
    label: "Disease Challenge",
    icon: ClipboardPaste,
    section: "Breeding Program",
    permission: PERMISSIONS.DISEASECHALLENGE_VIEW,
  },
  {
    to: "/genetic-broodstock",
    label: "Genetic & Broodstock",
    icon: Shrimp,
    section: "Breeding Program",
    permission: PERMISSIONS.BREEDGENTANK_VIEW,
  },
  {
    to: "/algae-tbd",
    label: "Breeding Algae (TBD)",
    icon: Circle,
    section: "Breeding Algae (TBD)",
    // permission: PERMISSIONS.BREEDALGAE_VIEW, // Removed in latest API
  },
  {
    to: "/companies",
    label: "Companies",
    icon: Building2,
    section: "Company Management",
    permission: PERMISSIONS.COMPANY_VIEW,
  },
  {
    to: "/companies/clients",
    label: "Client",
    icon: Building2,
    section: "Company Management",
    end: false,
    permission: PERMISSIONS.CLIENT_VIEW,
  },
  {
    to: "/companies/facilities",
    label: "Facilities",
    icon: Building,
    section: "Company Management",
    end: false,
    permission: PERMISSIONS.FACILITY_VIEW,
  },
  {
    to: "/companies/units",
    label: "Units",
    icon: Warehouse,
    section: "Company Management",
    end: false,
    permission: PERMISSIONS.UNIT_VIEW,
  },
  {
    to: "/companies/pit-tags",
    label: "PIT Tags",
    icon: Tag,
    section: "Company Management",
    end: false,
    permission: PERMISSIONS.TAG_VIEW,
  },
  {
    to: "/tasks/schedule-template",
    label: "Schedule Template",
    icon: ClipboardPenLine,
    section: "Task Management",
    permission: PERMISSIONS.SCHEDULETEMPLATE_VIEW,
  },
  {
    to: "/tasks/schedule-group-tasks",
    label: "Schedule Group Tasks",
    icon: ClipboardClock,
    section: "Task Management",
    permission: PERMISSIONS.SCHEDULETASK_VIEW,
  },
  {
    to: "/tasks/redelegate",
    label: "Redelegate Tasks",
    icon: ClipboardPaste,
    section: "Task Management",
    permission: PERMISSIONS.REDELEGATETASK_VIEW,
  },
  {
    to: "/users",
    label: "Users",
    icon: User,
    section: "User Management",
    permission: PERMISSIONS.USER_VIEW,
  },
  {
    to: "/users/roles",
    label: "Role",
    icon: UserCog,
    section: "User Management",
    permission: PERMISSIONS.ROLES_VIEW,
  },
  {
    label: "Breeding Maturation",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/maturation/mating-tank",
        label: "Mating per Tank Breakdown",
        permission: PERMISSIONS.REPORT_BREEDING_MATURATION_MATING_PER_TANK,
      },
      {
        to: "/reports/breeding-maturation/total-and-average-mating-per-tank",
        label: "Total & Average Mating per Tank",
        permission: PERMISSIONS.REPORT_BREEDING_MATURATION_TOTAL_AVG_MATING,
      },
      {
        to: "/reports/maturation/percentage-mating-mortality",
        label: "% Mating Total & Mortality",
        permission:
          PERMISSIONS.REPORT_BREEDING_MATURATION_MATING_TOTAL_AND_MORTALITY,
      },
      {
        to: "/reports/maturation/daily-feeding",
        label: "Daily Feeding",
        permission: PERMISSIONS.REPORT_BREEDING_MATURATION_FEEDING_DAILY,
      },
    ],
  },
  {
    label: "Breeding Hatching",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/breeding-hatching/avg-egg-and-nauplii",
        label: "Avg Egg & Nauplii",
        permission: PERMISSIONS.REPORT_BREEDING_HATCHING_AVG_EGG_NAUPLI,
      },
      {
        to: "/reports/breeding-hatching/avg-fertil-and-hatching",
        label: "Avg Fertil & Hatching %",
        permission: PERMISSIONS.REPORT_BREEDING_HATCHING_AVG_FERTIL_HATCHING_PERCENTAGE,
      },
    ],
  },
  {
    label: "Breeding Larvae",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/breeding-larvae/avg-pl10-population",
        label: "Avg PL10 Population",
        permission: PERMISSIONS.REPORT_BREEDING_LARVAE_AVG_PL10_POPULATION,
      },
      {
        to: "/reports/breeding-larvae/pl4-pl10-population-and-sr",
        label: "PL4 - PL10 Population & SR",
        permission: PERMISSIONS.REPORT_BREEDING_LARVAE_POPULATION_AND_SR_PL4_ESTIMATE,
      },
      {
        to: "/reports/breeding-larvae/pl-trial-batch",
        label: "PL Trial Batch",
        permission: PERMISSIONS.REPORT_BREEDING_MATURATION_PL_TRIAL_BATCH,
      },
      {
        to: "/reports/breeding-larvae/tank-temperature",
        label: "Larvae Tank Temperature",
        permission: PERMISSIONS.REPORT_BREEDING_LARVAE_LARVAE_TEMPERATURE,
      },
    ],
  },
  {
    label: "Breeding Growout",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/breeding-growout/avg-weekly-grow-rate-batch",
        label: "Avg Weekly Grow Rate Batch",
        permission: PERMISSIONS.REPORT_BREEDING_GROWOUT_AVG_WEIGHT,
      },
      {
        to: "/reports/breeding-growout/avg-daily-gain-per-batch",
        label: "Avg Daily Gain per Batch",
        permission: PERMISSIONS.REPORT_BREEDING_GROWOUT_AVG_ADG,
      },
      {
        to: "/reports/breeding-growout/sample-submission",
        label: "Sample Submission",
      },
    ],
  },
  {
    label: "PL Trials",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/pl-trial/sample-submission",
        label: "Sample Submission",
      },
    ],
  },
  {
    label: "Cage Trials",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/cage-trial/weight-sr",
        label: "Weight & SR",
        permission: PERMISSIONS.REPORT_CAGETRIAL_WEIGHT_SR,
      },
      {
        to: "/reports/cage-trial/cage-deformities",
        label: "Cage Deformities",
        permission: PERMISSIONS.REPORT_CAGETRIAL_CAGE_DEFORMITIES,
      },
      {
        to: "/reports/cage-trial/all-cages-deformities",
        label: "All Cage Deformities",
        permission: PERMISSIONS.REPORT_CAGETRIAL_ALL_CAGE_DEFORMITIES,
      },
      {
        to: "/reports/cage-trial/nbc-deformities",
        label: "NBC Deformities",
        permission: PERMISSIONS.REPORT_CAGETRIAL_NBC_DEFORMITIES,
      },
      {
        to: "/reports/cage-trial/tagging-shipping",
        label: "Tagging & Shipping",
        permission: PERMISSIONS.REPORT_CAGETRIAL_TAGGING_SHIPPING,
      },
      {
        to: "/reports/cage-trial/sample-submission",
        label: "Sample Submission",
      },
    ],
  },
  {
    label: "Disease Challenge",
    icon: File,
    section: "Reports",
    children: [
      {
        to: "/reports/disease-challenge/weight-sr",
        label: "Weight & SR",
        permission: PERMISSIONS.REPORT_DISEASE_CHALLENGE_WEIGHT_SR,
      },
      {
        to: "/reports/disease-challenge/cage-deformities",
        label: "Cage Deformities",
        permission: PERMISSIONS.REPORT_DISEASE_CHALLENGE_CAGE_DEFORMITIES,
      },
      {
        to: "/reports/disease-challenge/all-cages-deformities",
        label: "All Cage Deformities",
        permission: PERMISSIONS.REPORT_DISEASE_CHALLENGE_ALL_CAGE_DEFORMITIES,
      },
      {
        to: "/reports/disease-challenge/sample-submission",
        label: "Sample Submission",
      },
    ],
  },
];
